/**
 * SocketService - Singleton para gestión de conexión WebSocket
 * 501 Dart League Online
 * 
 * Gestiona la conexión Socket.IO con el backend, autenticación JWT
 * y reconexión automática. Un solo punto de acceso para toda la app.
 * 
 * USO:
 *   import socketService from './socketService';
 *   socketService.connect();
 *   socketService.on('lobby:players', (players) => { ... });
 *   socketService.disconnect();
 */

import { io } from 'socket.io-client';
import api from './api';

// URL del servidor (sin /api, Socket.IO va en la raíz)
const DEFAULT_BACKEND_ORIGIN = 'https://501backend-production.up.railway.app';

const normalizeUrl = (url) => (url || '').trim().replace(/\/+$/, '');

const configuredSocketUrl = normalizeUrl(process.env.REACT_APP_SOCKET_URL);
const configuredBackendOrigin = normalizeUrl(process.env.REACT_APP_BACKEND_URL);
const configuredApiUrl = normalizeUrl(process.env.REACT_APP_API_URL);

const derivedFromApiUrl = configuredApiUrl.endsWith('/api')
  ? configuredApiUrl.slice(0, -4)
  : configuredApiUrl;

const SOCKET_URL = configuredSocketUrl
  || configuredBackendOrigin
  || derivedFromApiUrl
  || DEFAULT_BACKEND_ORIGIN;

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map(); // eventName -> Set<callback>
    this._connected = false;
    this._connecting = false;
    this._refreshingAuth = false;
  }

  /**
   * Conectar al servidor Socket.IO con JWT
   * Se llama una vez al entrar al lobby online
   */
  connect() {
    // Leer token fresco siempre
    const token = localStorage.getItem('dartleague_token');
    if (!token) {
      console.error('[SocketService] No hay token JWT para conectar');
      return null;
    }

    // Si ya hay socket y está conectado, solo actualizar el token para la próxima reconexión
    if (this.socket?.connected) {
      this.socket.auth = { token };
      return this.socket;
    }

    // Si ya está en proceso de conexión, no crear otro
    if (this._connecting) return this.socket;

    // Si hay un socket existente pero desconectado, destruirlo y crear uno nuevo
    // (para evitar reconectar con token expirado del handshake original)
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this._connecting = true;

    this.socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 30,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    // Actualizar token ANTES de cada reconexión (socket.io lo lee de socket.auth)
    this.socket.on('reconnect_attempt', () => {
      const freshToken = localStorage.getItem('dartleague_token');
      if (freshToken) {
        this.socket.auth = { token: freshToken };
      }
    });

    // ─── Eventos internos ───
    this.socket.on('connect', () => {
      this._connected = true;
      this._connecting = false;
      console.log('[SocketService] Conectado:', this.socket.id);
    });

    this.socket.on('disconnect', (reason) => {
      this._connected = false;
      console.log('[SocketService] Desconectado:', reason);
    });

    this.socket.on('connect_error', (error) => {
      this._connecting = false;
      this._connected = false;
      console.error('[SocketService] Error de conexión:', error.message);

      const authError = error?.message;

      if (['AUTH_REQUIRED', 'TOKEN_EXPIRED', 'AUTH_FAILED', 'INVALID_TOKEN'].includes(authError) && !this._refreshingAuth) {
        this._refreshingAuth = true;
        void api.refreshAccessToken()
          .then((refreshed) => {
            this._refreshingAuth = false;

            if (refreshed) {
              this.reconnect();
              return;
            }

            this.disconnect();
          })
          .catch((refreshError) => {
            this._refreshingAuth = false;
            console.error('[SocketService] Error refrescando token para socket:', refreshError);
            this.disconnect();
          });
        return;
      }

      // Si el token expiró o es inválido, limpiar socket para que el próximo connect() cree uno nuevo
      if (['AUTH_REQUIRED', 'TOKEN_EXPIRED', 'AUTH_FAILED', 'INVALID_TOKEN', 'ONLINE_ACCESS_DENIED'].includes(authError)) {
        console.error('[SocketService] Error de autenticación, deteniendo reconexión');
        this.socket.disconnect();
        // NO destruir this.socket aquí — dejar que el próximo connect() lo haga
      }
    });

    this.socket.on('force-disconnect', ({ reason }) => {
      console.warn('[SocketService] Expulsado:', reason);
      this.disconnect();
    });

    // Re-registrar listeners guardados (por si hubo reconexión)
    for (const [event, callbacks] of this.listeners) {
      for (const cb of callbacks) {
        this.socket.on(event, cb);
      }
    }

    return this.socket;
  }

  /**
   * Desconectar limpiamente (limpia listeners → usar al salir del lobby)
   */
  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this._connected = false;
    this._connecting = false;
    this._refreshingAuth = false;
    this.listeners.clear();
  }

  /**
   * Reconectar preservando listeners (usar en "Reintentar")
   * Desconecta el socket actual y crea uno nuevo,
   * re-registrando todos los listeners existentes.
   */
  reconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this._connected = false;
    this._connecting = false;
    this._refreshingAuth = false;
    // NO limpiar this.listeners → se re-registran en connect()
    return this.connect();
  }

  /**
   * Registrar listener para un evento
   * Se mantiene en memoria para re-registrar tras reconexión
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    if (this.socket) {
      this.socket.on(event, callback);
    }

    // Retornar función de cleanup para useEffect
    return () => this.off(event, callback);
  }

  /**
   * Eliminar un listener específico
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
      if (this.listeners.get(event).size === 0) {
        this.listeners.delete(event);
      }
    }

    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  /**
   * Emitir evento al servidor
   */
  emit(event, data) {
    if (!this.socket?.connected) {
      console.warn('[SocketService] No conectado, no se puede emitir:', event);
      return;
    }
    this.socket.emit(event, data);
  }

  /**
   * ¿Está conectado?
   */
  get isConnected() {
    return this.socket?.connected || false;
  }

  /**
   * ID del socket actual
   */
  get id() {
    return this.socket?.id || null;
  }
}

// Singleton
const socketService = new SocketService();
export default socketService;
